import React, { useEffect, useState, useRef } from "react";
import Map, { Marker, Source, Layer } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { TbPlus, TbMinus } from "react-icons/tb";
import { HiddenMapGeolocateControl, MapGeolocateButton } from "./MapGeolocate";
import { BsLayers } from "react-icons/bs";

const SingleView = ({ lon, lat, type, updatePosition, style }) => {
  const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || "RUNTIME_REPLACE_REACT_APP_MAPBOX_TOKEN";
  const [viewState, setViewState] = useState({
    latitude: lat == null || lon == null ? 40.7334 : lat,
    longitude: lat == null || lon == null ? -82.7899 : lon,
    zoom: lat == null || lon == null ? 6 : 13,
  });
  const [isSelecting, setIsSelecting] = useState(false); // To track if selection mode is active
  const [selectedPosition, setSelectedPosition] = useState({longitude: lon, latitude: lat}); // To store the selected position
  const [mapViewStyle, setMapViewStyle] = useState('mapbox://styles/mapbox/outdoors-v12');
  const [mapStyle, setMapStyle] = useState('outdoors');
  const [showStyles, setShowStyles] = useState(false);
  const [showRoads, setShowRoads] = useState(true);
  const [showLabels, setShowLabels] = useState(true);
  const [showBoundaries, setShowBoundaries] = useState(true);
  const mapRef = useRef(null);
  const geolocateRef = useRef(null);

  const handleMapClick = (event) => {
    if (isSelecting) {
      const { lng, lat } = event.lngLat;
      setSelectedPosition({ longitude: lng, latitude: lat });
      updatePosition?.(lng.toFixed(6), lat.toFixed(6));
      setIsSelecting(false); // Exit selection mode
    }
  };

  const toggleSelectionMode = () => {
    setIsSelecting((prev) => !prev); // Toggle selection mode
  };

  const changeZoom = (value) => {
    if (mapRef.current) {
      const map = mapRef.current.getMap(); // Get the Mapbox instance
      const newZoom = map.getZoom() + value;

      map.flyTo({
        zoom: newZoom,
        duration: 1000, // Smooth animation duration in ms
        essential: true, // Ensures animation always happens
      });
    }
  }

  useEffect(() => {
    setViewState({
      latitude: lat == null || lon == null ? 40.7334 : lat,
      longitude: lat == null || lon == null ? -82.7899 : lon,
      zoom: lat == null || lon == null ? 6 : 13,
    })
    setSelectedPosition({ longitude: lon, latitude: lat});
  }, [lon, lat]);

  useEffect(() => {
    if(mapStyle == 'streets') setMapViewStyle('mapbox://styles/mapbox/streets-v12');
    if(mapStyle == 'satellite') setMapViewStyle('mapbox://styles/mapbox/satellite-v9');
    if(mapStyle == 'outdoors') setMapViewStyle('mapbox://styles/mapbox/outdoors-v12');
  }, [mapStyle])

  return (
    <div className="map-frame" style={style}>
      <button
        onClick={toggleSelectionMode}
        style={{
          position: "absolute",
          zIndex: 1,
          top: 10,
          left: 10,
          padding: "10px 20px",
          background: isSelecting ? "red" : "blue",
          color: "white",
          border: "none",
          borderRadius: "5px",
          cursor: "pointer",
        }}
      >
        {isSelecting ? "Cancel Selection" : "Select Position"}
      </button>
      <Map
        ref={mapRef}
        {...viewState}
        onMove={(evt) => setViewState(evt.viewState)}
        style={{
          width: "100%",
          height: "100%",
        }}
        mapStyle={mapViewStyle}
        mapboxAccessToken={MAPBOX_TOKEN}
        onClick={handleMapClick}
        cursor={isSelecting ? "crosshair" : "grab"}
      >
        <HiddenMapGeolocateControl ref={geolocateRef} />
        {mapStyle == 'satellite' && showRoads && <Source
          id="composite"
          type="vector"
          url="mapbox://mapbox.mapbox-streets-v8"
        >
          <Layer
            id="roads"
            type="line"
            source-layer="road"
            paint={{
              "line-color": "#ffd700",
              "line-width": 1,
            }}
          />
        </Source>}

        {mapStyle == 'satellite' && showBoundaries && <Source
          id="state-borders"
          type="vector"
          url="mapbox://mapbox.mapbox-streets-v8"
        >
          <Layer
            id="state-lines"
            type="line"
            source-layer="admin"
            filter={["all", ["==", "admin_level", 1]]} // Corrected filter syntax
            paint={{
              "line-color": "#00ff00", // Green color for state lines
              "line-width": 2,
            }}
          />
        </Source>}
        
        {mapStyle == 'satellite' && showLabels && <Source
          id="city-labels"
          type="vector"
          url="mapbox://mapbox.mapbox-streets-v8"
        >
          <Layer
            id="city-labels"
            type="symbol"
            source-layer="place_label"
            layout={{
              "text-field": "{name_en}", // Label with name
              "text-size": 12,
            }}
            paint={{
              "text-color": "#ffffff", // White color for text
              "text-halo-color": "#000000", // Black halo for visibility
              "text-halo-width": 1,
            }}
          />
        </Source>}
        <Marker latitude={selectedPosition.latitude} longitude={selectedPosition.longitude}>
          {type == 'crane' && 
            <img src="/assets/marker-crane.png" width={30}/>
          }
          {type == 'prospect' && 
            <img src="/assets/marker-prospect.png" width={30}/>
          }
        </Marker>
      </Map>
      <div className="map-controls">
        <div className="navigation-control">
          <button className="zoom-in">
            <TbPlus className="zoom-in-icon" onClick={() => changeZoom(1)}/>
          </button>
          <button className="zoom-out">
            <TbMinus className="zoom-out-icon" onClick={() => changeZoom(-1)}/>
          </button>
          <MapGeolocateButton controlRef={geolocateRef} />
        </div>
        <div className="style-control">
          <button className="style-selector" onClick={() => setShowStyles((prev) => !prev)}>
            <BsLayers className="style-selector-icon"/>
          </button>
          {showStyles && <div className="map-styles">
            <div className="main-styles">
              <div className={`map-style-button ${mapStyle === "streets" ? "map-style-button-active" : ""}`} onClick={() => setMapStyle('streets')}>Streets</div>
              <div className={`map-style-button ${mapStyle === "outdoors" ? "map-style-button-active" : ""}`}  onClick={() => setMapStyle('outdoors')}>Outdoors</div>
              <div className={`map-style-button ${mapStyle === "satellite" ? "map-style-button-active" : ""}`}  onClick={() => setMapStyle('satellite')}>Satellite</div>
            </div>
            {mapStyle == 'satellite' && <div className="source-styles">
              <div className={`map-style-button ${showRoads ? "map-style-button-active" : ""}`} onClick={() => setShowRoads((prev) => !prev)}>Roads</div>
              <div className={`map-style-button ${showLabels ? "map-style-button-active" : ""}`} onClick={() => setShowLabels((prev) => !prev)}>Labels</div>
              <div className={`map-style-button ${showBoundaries ? "map-style-button-active" : ""}`} onClick={() => setShowBoundaries((prev) => !prev)}>Boundaries</div>
            </div>}
          </div>}
        </div>
      </div>
    </div>
  );
};

export default SingleView;
